import React, { useState, useEffect } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { Menu, X, Phone, MessageCircle, MapPin, Instagram, Facebook } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { waLink, api } from "@/lib/api";

const LOGO_URL = "https://customer-assets-m6fa6gv7.emergentagent.net/job_bufnita-melissa/artifacts/2wo8tnht_logo%20final%202026.png";

const NAV = [
  { label: "Acasă", to: "/" },
  { label: "Vizitează-ne", to: "/viziteaza" },
  { label: "Animalele noastre", to: "/animale" },
  { label: "Tururi și bilete", to: "/tururi" },
  { label: "Școli și grupuri", to: "/scoli-si-grupuri" },
  { label: "Povestea noastră", to: "/povestea-noastra" },
  { label: "Susține-ne", to: "/sustine-ne" },
  { label: "Contact", to: "/contact" },
];

export function StatusBanner() {
  const [msg, setMsg] = useState("");
  useEffect(() => {
    api.get("/status-banner").then(r => setMsg(r.data.message || "")).catch(() => {});
  }, []);
  if (!msg) return null;
  return (
    <div data-testid="status-banner" className="bg-forest-900 text-cream-50 text-xs sm:text-sm py-2 px-4 text-center font-medium">
      {msg}
    </div>
  );
}

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const isHome = loc.pathname === "/";
  const transparent = isHome && !scrolled;

  return (
    <>
      <StatusBanner />
      <header data-testid="site-header" className={`sticky top-0 z-40 transition-all duration-300 ${transparent ? "bg-transparent" : "bg-cream-50/95 backdrop-blur border-b border-forest-900/10"}`}>
        <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8 h-16 lg:h-20 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Habitatul Meu" className="h-10 lg:h-14 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {NAV.slice(0, 6).map(n => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1) || 'home'}`} className={`text-sm font-medium hover:text-tucan transition-colors ${transparent ? "text-white" : "text-forest-900"}`}>
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href={waLink()} target="_blank" rel="noreferrer" data-testid="header-whatsapp" className="p-2 rounded-full hover:bg-forest-900/10 transition">
              <MessageCircle className={transparent ? "text-white w-5 h-5" : "text-forest-900 w-5 h-5"} />
            </a>
            <Link to="/tururi" data-testid="header-cta-book" className="bg-tucan text-white rounded-full px-6 py-3 font-semibold hover:bg-[#D97A29] transition-colors">
              Rezervă un tur
            </Link>
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-trigger" className={`lg:hidden p-2 rounded-full ${transparent ? "text-white" : "text-forest-900"}`}>
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-cream-50 border-forest-900/10 w-[85%] sm:w-[400px]">
              <div className="flex flex-col gap-1 mt-8">
                {NAV.map(n => (
                  <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.to.slice(1) || 'home'}`} className="py-3 border-b border-forest-900/10 text-forest-900 font-medium text-lg">
                    {n.label}
                  </Link>
                ))}
                <Link to="/tururi" data-testid="mobile-cta-book" className="mt-6 bg-tucan text-white rounded-full py-4 text-center font-semibold">
                  Rezervă un tur
                </Link>
                <a href={waLink()} target="_blank" rel="noreferrer" data-testid="mobile-whatsapp" className="mt-3 bg-[#25D366] text-white rounded-full py-4 text-center font-semibold flex items-center justify-center gap-2">
                  <MessageCircle className="w-5 h-5" /> WhatsApp
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>
    </>
  );
}

export function MobileStickyCta() {
  const loc = useLocation();
  if (loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/rezervare")) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-cream-50 border-t border-forest-900/10 p-3 flex gap-2 z-30 shadow-[0_-4px_16px_rgba(0,0,0,0.05)]">
      <Link to="/tururi" data-testid="sticky-cta-book" className="flex-1 bg-tucan text-white rounded-full py-3 font-semibold text-center">
        Rezervă un tur
      </Link>
      <a href={waLink()} target="_blank" rel="noreferrer" data-testid="sticky-whatsapp" className="bg-[#25D366] text-white rounded-full px-5 py-3 font-semibold flex items-center gap-2">
        <MessageCircle className="w-5 h-5" />
      </a>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="bg-forest-900 text-cream-50 mt-20 pb-24 lg:pb-12">
      <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 grid grid-cols-1 md:grid-cols-4 gap-8">
        <div>
          <img src={LOGO_URL} alt="Habitatul Meu" className="h-14 w-auto bg-cream-50 rounded-lg p-2 mb-4" />
          <p className="text-sm text-cream-50/80">Asociația Habitatul Meu — CUI 40143182. Bird & Wildlife Park · Dumești, Iași.</p>
        </div>
        <div>
          <h4 className="text-cream-50 mb-3 font-semibold">Navigație</h4>
          <ul className="space-y-2 text-sm">
            {NAV.slice(0, 6).map(n => <li key={n.to}><Link to={n.to} className="text-cream-50/80 hover:text-tucan">{n.label}</Link></li>)}
          </ul>
        </div>
        <div>
          <h4 className="text-cream-50 mb-3 font-semibold">Contact</h4>
          <ul className="space-y-2 text-sm text-cream-50/80">
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0" /> Strada Victoriei 191, Dumești, Iași</li>
            <li className="flex items-center gap-2"><Phone className="w-4 h-4" /> +40 741 142 142</li>
            <li className="flex items-center gap-2"><MessageCircle className="w-4 h-4" /> <a href={waLink()} target="_blank" rel="noreferrer" className="hover:text-tucan">WhatsApp</a></li>
          </ul>
        </div>
        <div>
          <h4 className="text-cream-50 mb-3 font-semibold">Social</h4>
          <div className="flex gap-3">
            <a href="https://www.facebook.com/ferma.pasarideornament/" target="_blank" rel="noreferrer" className="p-2 rounded-full bg-cream-50/10 hover:bg-tucan transition"><Facebook className="w-5 h-5" /></a>
            <a href="https://www.instagram.com/habitatulmeubp/" target="_blank" rel="noreferrer" className="p-2 rounded-full bg-cream-50/10 hover:bg-tucan transition"><Instagram className="w-5 h-5" /></a>
          </div>
          <div className="mt-6 space-y-1 text-xs text-cream-50/60">
            <Link to="/intrebari-frecvente" className="block hover:text-tucan">Întrebări frecvente</Link>
            <Link to="/politica-confidentialitate" className="block hover:text-tucan">Confidențialitate</Link>
            <Link to="/termeni-si-conditii" className="block hover:text-tucan">Termeni</Link>
          </div>
        </div>
      </div>
      <div className="border-t border-cream-50/10 py-4 text-center text-xs text-cream-50/60">
        © {new Date().getFullYear()} Asociația Habitatul Meu · Toate drepturile rezervate.
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <MobileStickyCta />
      <Footer />
    </div>
  );
}
